/**
 * Die beiden Ergebnisspalten einer Kalkulationszeile - „EK DE" und „EK neu"
 * (`index.html:10302-10303`), jeweils als EIN Feld, das den wirksamen Wert zeigt und beim Tippen die
 * Ueberschreibung schreibt.
 *
 * Die Komponente liefert reine `<td>`-Geschwister und steht darum ausschliesslich innerhalb der
 * Zeile in `PositionsTabelle.tsx`.
 *
 * ================= WARUM EIN FELD UND NICHT ZWEI (Entscheidung, nachgezogen) =====================
 *
 * Der Task-21-Brief verlangte zwei beschriftete Felder je Spalte: den gerechneten Wert (`r.ekDeAuto`,
 * `readOnly`, `EKF`-formatiert) getrennt von der Ueberschreibung (`it.ekDeOverride`). Das ist hier
 * ZURUECKGENOMMEN. Begruendung:
 *
 * 1. Die **optische Paritaet** zum Bestand ist in diesem Projekt ein PRUEFBARES Kriterium der
 *    Frontend-Spec (Bildvergleich + DOM-Signatur gegen die aus `index.html` aufgenommene Baseline).
 *    Sie steht als Global Constraint UEBER dem Text einer einzelnen Aufgabe. Zwei Felder je Spalte
 *    aendern Knotenzahl und Spaltenbreite und machen den Vergleich `drawer-calc` rot.
 * 2. „Zwei beschriftete Felder je Spalte" hat im Bestand KEIN Gegenstueck - es ist eine Erfindung des
 *    Briefs. Genau das verbietet die Woertlichkeitsregel dieser Phase: portiert wird, was da ist.
 * 3. Der bequeme Ausweg - ein Eintrag im Ausnahmeregister (`test/visual/ausnahmen.ts`) - haette die
 *    Abweichung dauerhaft festgeschrieben statt sie zu beheben. `ausnahmen.ts` ist unangetastet.
 *
 * Der Bestand, woertlich (`index.html:10302`):
 *
 *     <td class="ek-num ek-result-cell"><input id="ekDe-${r.i}" type="number" min="0" step="0.0001"
 *       value="${inputNumber(r.ekDe)}" oninput="patchItemCalcLive(${r.i},'ekDeOverride',this.value)"
 *       onblur="refreshCalcDerived()"></td>
 *
 * Also: ANGEZEIGT wird der WIRKSAME Wert (`r.ekDe` = Ueberschreibung, falls gesetzt, sonst
 * `r.ekDeAuto`), GESCHRIEBEN wird die Ueberschreibung. Gerechneter und eingetippter Wert stehen in
 * demselben Kasten, und ob der Wert gerechnet oder eingetippt ist, sieht man ihm nicht an - eine
 * Eigenheit des Bestands, GEMELDET und nicht repariert. `onblur="refreshCalcDerived()"` hat hier
 * keinen Gegenpart (siehe `EkTab.tsx`).
 *
 * ================= Kein `aria-label`, keine `id` - Zugriff ueber die Spaltenbeschriftung ==========
 *
 * Das Feld traegt weder `aria-label` noch die `id` des Bestands. Beides waere in `domSignatur`
 * unsichtbar (dort sind `id`, `title` und `aria-*` ausdruecklich nicht erfasst), aber ein
 * `aria-label` waere eine ERFINDUNG am portierten Markup - dieselbe Lage, die
 * `test/komponenten/Drawer/feldZugriff.ts` fuer die `.field`-Beschriftungen des Reiters „Bestellung"
 * schon entschieden hat: gemeldet, nicht repariert. Die Tests greifen darum ueber die
 * SPALTENBESCHRIFTUNG des `<thead>` zu, so wie die Beschriftung auch fuer einen sehenden Benutzer
 * wirkt (`spalteInZeile` in `ekTab.test.tsx`).
 *
 * GEMELDETE FOLGE: die 22 Spalten dieser Tabelle sind fuer einen Bildschirmleser nicht benennbar -
 * kein `<th scope>`-Bezug, kein `aria-label`, nur Position. Das ist ein Befund am Bestand.
 *
 * ================= RECHTE: hier prueft der Reiter selbst, anders als Task 17/18 ==================
 *
 * `BestellungTab` und `TasksTab` pruefen bewusst KEIN Recht - gesperrt wird ausschliesslich durch
 * den Durchgang in `Drawer.tsx` (`index.html:9296-9304`), der alles in `.drawer-body` erfasst. Fuer
 * die Ueberschreibungen verlangt der Brief zusaetzlich eine EIGENE Sperre (`manage_costs`), und der
 * Nachweis dazu zeichnet `EkTab` OHNE den Rahmen. Beide Sperren sagen dasselbe:
 * `DRAWER_TAB_EDIT_PERMISSIONS.calc` ist `manage_costs` (`drawerRechte.ts`), und genau diese Quelle
 * fragt `EkTab` ueber `darfTabBearbeiten(rechte, "calc")` ab - es entsteht also keine zweite
 * Rechteregel, nur eine zweite Anwendung derselben. Die uebrigen Felder des Reiters folgen weiter
 * allein dem Durchgang.
 *
 * `disabled` ist das EINZIGE Attribut hier, das der Bestand nicht traegt. Es ist in `domSignatur`
 * nicht erfasst, und im Baseline-Zustand (Bootstrap-Admin, `manage_costs` vorhanden) laesst React es
 * ohnehin weg - der Bildvergleich sieht davon nichts.
 */
import { inputNumber } from "./PositionsTabelle.tsx";
import type { EkRow } from "../../../lib/landedCost.ts";

export interface UeberschreibungenProps {
  zeile: EkRow;
  /** `hasPermission('manage_costs')` - siehe Kopfkommentar, Abschnitt „Rechte". */
  darfBearbeiten: boolean;
  onPosition: (i: number, aenderung: Record<string, unknown>) => void;
}

export function Ueberschreibungen({ zeile: r, darfBearbeiten, onPosition }: UeberschreibungenProps) {
  const patch = (schluessel: string, wert: unknown): void => onPosition(r.i, { [schluessel]: wert });

  return (
    <>
      {/* `index.html:10302` - zeigt `r.ekDe`, schreibt `ekDeOverride`. */}
      <ErgebnisSpalte
        wirksam={r.ekDe}
        darfBearbeiten={darfBearbeiten}
        onUeberschreiben={(wert) => patch("ekDeOverride", wert)}
      />
      {/* `index.html:10303` - zeigt `r.ekNew`, schreibt `ekNewOverride`. */}
      <ErgebnisSpalte
        wirksam={r.ekNew}
        darfBearbeiten={darfBearbeiten}
        onUeberschreiben={(wert) => patch("ekNewOverride", wert)}
      />
    </>
  );
}

interface ErgebnisSpalteProps {
  /** Der WIRKSAME Wert: `r.ekDe` bzw. `r.ekNew` - Ueberschreibung, falls gesetzt, sonst Automatik. */
  wirksam: number;
  darfBearbeiten: boolean;
  onUeberschreiben: (wert: string) => void;
}

/** Eine Ergebnisspalte, ein Feld (`index.html:10302` bzw. `:10303`). */
function ErgebnisSpalte({ wirksam, darfBearbeiten, onUeberschreiben }: ErgebnisSpalteProps) {
  return (
    <td className="ek-num ek-result-cell">
      <input
        type="number"
        min="0"
        step="0.0001"
        value={inputNumber(wirksam)}
        disabled={!darfBearbeiten}
        onChange={(e) => onUeberschreiben(e.currentTarget.value)}
      />
    </td>
  );
}
